import { useState } from 'react';
import { FormControl, FormLabel, Select, VStack, Text, Box, Spinner } from '@chakra-ui/react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Modal from '@/trio-ui/Modal';

type CopilotTask = {
  id: string;
  title: string;
  taskType: string;
  priority: string;
  status: string;
};

type StartResult = {
  sessionId: string;
  status: string;
  firstCycle: { status: string; action: string | null; description: string | null; diagnosis: string | null; opportunitiesFound: number } | null;
  tasks: CopilotTask[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { targetPosition: number; maxCycles: number }) => Promise<StartResult>;
  keyword: string;
  currentPosition: number | null;
  targetPageSet: boolean;
  targetPageUrl: string | null;
  detectedPageUrl: string | null;
  onConfirmTargetPage: () => Promise<void>;
  wpConnected: boolean;
  gscConnected: boolean;
};

const priorityColor: Record<string, string> = {
  high: '#EF5B5B',
  medium: '#F4B400',
  low: '#71717A',
};

const taskTypeLabel: Record<string, string> = {
  title_tag: 'Title',
  meta_description: 'Meta',
  content: 'Content',
  schema: 'Schema',
  internal_link: 'Links',
  technical: 'Technical',
  backlink: 'Backlink',
  other: 'Other',
};

const ActivateCopilotModal = function ({ open, onClose, onSubmit, keyword, currentPosition, targetPageSet, targetPageUrl, detectedPageUrl, onConfirmTargetPage, wpConnected, gscConnected }: Props): JSX.Element {
  const [isConfirming, setIsConfirming] = useState(false);
  const [targetPosition, setTargetPosition] = useState(3);
  const [maxCycles, setMaxCycles] = useState(12);
  const [phase, setPhase] = useState<'config' | 'analyzing' | 'results'>('config');
  const [result, setResult] = useState<StartResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setPhase('analyzing');
    setError(null);
    try {
      const res = await onSubmit({ targetPosition, maxCycles });
      setResult(res);
      setPhase('results');
    } catch (err) {
      setError((err as Error).message);
      setPhase('config');
    }
  };

  const handleClose = () => {
    setPhase('config');
    setResult(null);
    setError(null);
    onClose();
  };

  // --- ANALYZING STATE ---
  if (phase === 'analyzing') {
    const body = (
      <VStack spacing={6} align="center" py={8}>
        <Spinner size="lg" color="#054AD9" thickness="3px" />
        <VStack spacing={1}>
          <Text fontSize="16px" fontWeight={500} color="#09090B">
            Analyzing "{keyword}"...
          </Text>
          <Text fontSize="13px" color="#71717A" textAlign="center">
            Checking ranking opportunities, generating optimization tasks, and preparing the first action.
          </Text>
        </VStack>
      </VStack>
    );

    return (
      <Modal
        isOpen={open}
        onClose={() => {}}
        modalTitle="SEO Copilot"
        headerIcon={AutoFixHighIcon}
        bodyRender={body}
        hideCancelButton
        hideConfirmButton
        isCentered
        size="md"
      />
    );
  }

  // --- RESULTS STATE ---
  if (phase === 'results' && result) {
    const tasks = result.tasks ?? [];
    const cycle = result.firstCycle;

    const body = (
      <VStack spacing={4} align="stretch">
        <Box display="flex" alignItems="center" gap="10px">
          <Box w="32px" h="32px" borderRadius="50%" bg="#0D9D5815" display="flex" alignItems="center" justifyContent="center">
            <CheckCircleIcon sx={{ fontSize: 20, color: '#0D9D58' }} />
          </Box>
          <Box>
            <Text fontSize="14px" fontWeight={500} color="#09090B">
              Copilot activated for "{keyword}"
            </Text>
            <Text fontSize="12px" color="#71717A">
              {cycle?.opportunitiesFound ?? 0} opportunities found · {tasks.length} tasks created
            </Text>
          </Box>
        </Box>

        {cycle?.diagnosis && (
          <Box bg="#F4F4F5" borderRadius="8px" p={3}>
            <Text fontSize="12px" color="#71717A" mb={1} fontWeight={500}>Diagnosis</Text>
            <Text fontSize="13px" color="#4A4B4E" lineHeight="20px">{cycle.diagnosis}</Text>
          </Box>
        )}

        {tasks.length > 0 && (
          <Box>
            <Text fontSize="12px" color="#71717A" mb={2} fontWeight={500} textTransform="uppercase" letterSpacing="0.5px">
              Optimization Plan
            </Text>
            <VStack spacing={1.5} align="stretch">
              {tasks.map((task, i) => (
                <Box
                  key={task.id}
                  display="flex"
                  alignItems="center"
                  gap="10px"
                  p="10px 12px"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor={task.status === 'done' ? '#0D9D5830' : 'hsla(0,0%,0%,0.08)'}
                  bg={task.status === 'done' ? '#0D9D5808' : 'white'}
                >
                  <Box
                    w="22px" h="22px" borderRadius="50%" flexShrink={0}
                    display="flex" alignItems="center" justifyContent="center"
                    bg={task.status === 'done' ? '#0D9D58' : '#F4F4F5'}
                  >
                    <Text fontSize="11px" fontWeight={500} color={task.status === 'done' ? 'white' : '#71717A'}>
                      {task.status === 'done' ? '✓' : i + 1}
                    </Text>
                  </Box>

                  <Box flex={1}>
                    <Text fontSize="13px" fontWeight={500} color="#09090B">{task.title}</Text>
                  </Box>

                  <Box display="flex" gap="6px" flexShrink={0}>
                    <Box px="8px" py="2px" borderRadius="4px" fontSize="10px" fontWeight={500} bg="#F4F4F5" color="#71717A">
                      {taskTypeLabel[task.taskType] ?? task.taskType}
                    </Box>
                    <Box
                      px="8px" py="2px" borderRadius="4px" fontSize="10px" fontWeight={500}
                      bg={`${priorityColor[task.priority] ?? '#71717A'}15`}
                      color={priorityColor[task.priority] ?? '#71717A'}
                    >
                      {task.priority}
                    </Box>
                  </Box>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        <Box borderRadius="8px" border="1px solid" borderColor="#E4E4E7" p={3}>
          <Text fontSize="12px" fontWeight={500} color="#09090B" mb={1}>What happens next</Text>
          <Text fontSize="12px" color="#71717A" lineHeight="18px">
            The copilot will execute one task per week, apply it to WordPress, wait for Google to re-index, and measure the impact. You can track progress on the keyword page and the Copilot dashboard.
          </Text>
        </Box>
      </VStack>
    );

    return (
      <Modal
        isOpen={open}
        onClose={handleClose}
        modalTitle="SEO Copilot"
        headerIcon={AutoFixHighIcon}
        bodyRender={body}
        onConfirmClick={handleClose}
        onConfirmLabel="Done"
        hideCancelButton
        isCentered
        size="lg"
      />
    );
  }

  // --- CONFIG STATE (default) ---
  const body = (
    <VStack spacing={5} align="stretch">
      <Box bg="#F4F4F5" borderRadius="8px" p={4}>
        <Text fontSize="14px" fontWeight={500} color="#09090B" mb={1}>{keyword}</Text>
        <Text fontSize="13px" color="#71717A">
          Current position: {currentPosition != null ? `#${currentPosition.toFixed(1)}` : 'Not ranked'}
        </Text>
      </Box>

      <Box borderRadius="8px" border="1px solid" borderColor="#E4E4E7" p={4}>
        <Text fontSize="12px" fontWeight={500} color="#71717A" mb={3} textTransform="uppercase" letterSpacing="0.5px">
          Readiness Checklist
        </Text>
        <VStack spacing={2.5} align="stretch">
          {/* Target page */}
          <Box display="flex" alignItems="flex-start" gap="10px">
            <Box w="20px" h="20px" flexShrink={0} display="flex" alignItems="center" justifyContent="center" mt="1px">
              {targetPageSet ? (
                <CheckCircleIcon sx={{ fontSize: 18, color: '#0D9D58' }} />
              ) : (
                <WarningAmberIcon sx={{ fontSize: 18, color: '#F4B400' }} />
              )}
            </Box>
            <Box flex={1}>
              <Text fontSize="13px" fontWeight={500} color={targetPageSet ? '#09090B' : '#71717A'}>
                Target page
              </Text>
              {targetPageSet && targetPageUrl ? (
                <Box
                  mt="4px"
                  p="8px 12px"
                  borderRadius="6px"
                  bg="#F0FDF4"
                  border="1px solid #BBF7D0"
                  display="flex"
                  alignItems="center"
                  gap="8px"
                >
                  <CheckCircleIcon sx={{ fontSize: 14, color: '#0D9D58' }} />
                  <Text fontSize="12px" color="#09090B" fontWeight={500} noOfLines={1}>
                    {targetPageUrl.replace(/^https?:\/\/[^/]+/, '') || '/'}
                  </Text>
                </Box>
              ) : detectedPageUrl ? (
                <Box mt="4px">
                  <Box
                    p="8px 12px"
                    borderRadius="6px"
                    bg="#FFFBEB"
                    border="1px solid #FDE68A"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="8px"
                  >
                    <Box display="flex" alignItems="center" gap="6px" flex={1} minW={0}>
                      <WarningAmberIcon sx={{ fontSize: 14, color: '#D97706', flexShrink: 0 }} />
                      <Text fontSize="12px" color="#92400E" fontWeight={500} noOfLines={1}>
                        {detectedPageUrl.replace(/^https?:\/\/[^/]+/, '') || '/'}
                      </Text>
                    </Box>
                    <Box
                      as="button"
                      px="10px"
                      py="4px"
                      borderRadius="6px"
                      bg="#054AD9"
                      color="white"
                      fontSize="11px"
                      fontWeight={600}
                      cursor="pointer"
                      flexShrink={0}
                      _hover={{ bg: '#043eaf' }}
                      onClick={async () => {
                        setIsConfirming(true);
                        await onConfirmTargetPage();
                        setIsConfirming(false);
                      }}
                      disabled={isConfirming}
                    >
                      {isConfirming ? 'Saving...' : 'Confirm this page'}
                    </Box>
                  </Box>
                  <Text fontSize="11px" color="#71717A" mt="4px">
                    Auto-detected from ranking data. Click to confirm.
                  </Text>
                </Box>
              ) : (
                <Text fontSize="12px" color="#71717A" lineHeight="16px" mt="2px">
                  Set a target page so the copilot knows which page to optimize
                </Text>
              )}
            </Box>
          </Box>

          {/* GSC */}
          <Box display="flex" alignItems="flex-start" gap="10px">
            <Box w="20px" h="20px" flexShrink={0} display="flex" alignItems="center" justifyContent="center" mt="1px">
              {gscConnected ? (
                <CheckCircleIcon sx={{ fontSize: 18, color: '#0D9D58' }} />
              ) : (
                <WarningAmberIcon sx={{ fontSize: 18, color: '#F4B400' }} />
              )}
            </Box>
            <Box>
              <Text fontSize="13px" fontWeight={500} color={gscConnected ? '#09090B' : '#71717A'}>
                Google Search Console connected
              </Text>
              {!gscConnected && (
                <Text fontSize="12px" color="#71717A" lineHeight="16px" mt="2px">Connect GSC to get real ranking data and search metrics</Text>
              )}
            </Box>
          </Box>

          {/* WordPress */}
          <Box display="flex" alignItems="flex-start" gap="10px">
            <Box w="20px" h="20px" flexShrink={0} display="flex" alignItems="center" justifyContent="center" mt="1px">
              {wpConnected ? (
                <CheckCircleIcon sx={{ fontSize: 18, color: '#0D9D58' }} />
              ) : (
                <WarningAmberIcon sx={{ fontSize: 18, color: '#F4B400' }} />
              )}
            </Box>
            <Box>
              <Text fontSize="13px" fontWeight={500} color={wpConnected ? '#09090B' : '#71717A'}>
                WordPress connected
              </Text>
              {!wpConnected && (
                <Text fontSize="12px" color="#71717A" lineHeight="16px" mt="2px">Connect WordPress so the copilot can auto-apply changes</Text>
              )}
            </Box>
          </Box>
        </VStack>
      </Box>

      {error && (
        <Box bg="#EF5B5B10" borderRadius="8px" p={3}>
          <Text fontSize="13px" color="#EF5B5B">{error}</Text>
        </Box>
      )}

      <FormControl>
        <FormLabel fontSize="sm" fontWeight={500}>Target Position</FormLabel>
        <Select value={targetPosition} onChange={(e) => setTargetPosition(Number(e.target.value))} size="md">
          <option value={1}>Top 1 — first result</option>
          <option value={3}>Top 3 — above the fold</option>
          <option value={5}>Top 5 — first page, upper half</option>
          <option value={10}>Top 10 — first page</option>
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" fontWeight={500}>Max Optimization Cycles</FormLabel>
        <Select value={maxCycles} onChange={(e) => setMaxCycles(Number(e.target.value))} size="md">
          <option value={4}>4 cycles (~1 month)</option>
          <option value={8}>8 cycles (~2 months)</option>
          <option value={12}>12 cycles (~3 months)</option>
          <option value={24}>24 cycles (~6 months)</option>
        </Select>
      </FormControl>

      <Box borderRadius="8px" border="1px solid" borderColor="#E4E4E7" p={4}>
        <Text fontSize="13px" fontWeight={500} color="#09090B" mb={2}>The copilot will automatically:</Text>
        <VStack spacing={1.5} align="stretch">
          {[
            'Analyze why this keyword isn\'t ranking higher',
            'Create a prioritized optimization plan',
            'Execute one fix per week on WordPress',
            'Measure impact after each change',
            'Repeat until the target position is reached',
          ].map((step, i) => (
            <Box key={i} display="flex" alignItems="flex-start" gap="8px">
              <Box w="6px" h="6px" borderRadius="50%" bg="#054AD9" mt="6px" flexShrink={0} />
              <Text fontSize="13px" color="#4A4B4E" lineHeight="20px">{step}</Text>
            </Box>
          ))}
        </VStack>
      </Box>
    </VStack>
  );

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      modalTitle="Activate SEO Copilot"
      headerIcon={AutoFixHighIcon}
      bodyRender={body}
      onConfirmClick={handleSubmit}
      onConfirmLabel="Start Copilot"
      isCentered
      size="md"
    />
  );
};

export default ActivateCopilotModal;
